import { useEffect, useMemo, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import DataTable from "../../components/DataTable";
import { gtApi } from "../../api/generalTradeApi";
import { useFormat } from "../../../context/FormatContext";
import { useBusy } from "../../../context/BusyContext";
import { useMessageModal } from "../../contexts/MessageModalContext";
import type { ReceiveInType } from "../../types/generalTrade";
import "../../styles/generalTrade.css";

const today = () => new Date().toISOString().slice(0, 10);

interface CustomerPaymentEntryForm {
  customer_id: string;
  description: string;
  receive_in: ReceiveInType;
  payment_date: string;
  amount_received: string;
  receiver_name: string;
  bank_name: string;
  reference_number: string;
}

interface OpenInvoice {
  id: number;
  sales_invoice_number: string;
  invoice_date: string;
  due_date?: string | null;
  grand_total: number | string;
  paid_amount: number | string;
  outstanding_amount: number | string;
  payment_status: string;
}

interface KnockOffAllocation {
  sales_invoice_id: number;
  allocated_amount: number;
}

const createInitialForm = (): CustomerPaymentEntryForm => ({
  customer_id: "",
  description: "Payment for A/c",
  receive_in: "CASH",
  payment_date: today(),
  amount_received: "",
  receiver_name: "",
  bank_name: "",
  reference_number: "",
});

const round2 = (value: number) => Number(value.toFixed(2));

export default function CustomerPayments() {
  const { formatDateOnly, formatCurrency } = useFormat();

  const { runBusy } = useBusy();
  const { showModal } = useMessageModal();

  const [rows, setRows] = useState<any[]>([]);

  const [customers, setCustomers] = useState<any[]>([]);

  const [selected, setSelected] = useState<any>(null);

  const [adding, setAdding] = useState(false);

  const [invoices, setInvoices] = useState<OpenInvoice[]>([]);

  /*
   * IDs are kept in click order.
   *
   * This is important because knock-off should
   * happen in the same order selected by the user.
   */
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<number[]>([]);

  const [form, setForm] =
    useState<CustomerPaymentEntryForm>(createInitialForm());

  const [saving, setSaving] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const [payments, customerList] = await Promise.all([
        gtApi.payments(),
        gtApi.customers(),
      ]);

      setRows(Array.isArray(payments) ? payments : []);

      setCustomers(Array.isArray(customerList) ? customerList : []);
    } catch (error) {
      console.error("Failed to load Customer Payments:", error);

      setRows([]);
      setCustomers([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  /*
   * =========================
   * AUTOMATIC KNOCK-OFF
   * =========================
   *
   * The calculation is derived entirely from:
   *
   * 1. Amount Received
   * 2. Checkbox selection order
   * 3. Current invoice outstanding balance
   *
   * There is no manual allocation input.
   */
  const knockOff = useMemo(() => {
    const amountReceived = Math.max(Number(form.amount_received || 0), 0);

    let remaining = round2(amountReceived);

    const allocationMap = new Map<number, number>();

    for (const invoiceId of selectedInvoiceIds) {
      if (remaining <= 0) {
        allocationMap.set(invoiceId, 0);

        continue;
      }

      const invoice = invoices.find(
        (row) => Number(row.id) === Number(invoiceId),
      );

      if (!invoice) {
        continue;
      }

      const outstanding = Math.max(Number(invoice.outstanding_amount || 0), 0);

      const amountToApply = round2(Math.min(remaining, outstanding));

      allocationMap.set(invoiceId, amountToApply);

      remaining = round2(remaining - amountToApply);
    }

    const appliedAmount = round2(
      Array.from(allocationMap.values()).reduce((sum, value) => sum + value, 0),
    );

    return {
      allocationMap,
      appliedAmount,
      unappliedAmount: round2(Math.max(amountReceived - appliedAmount, 0)),
    };
  }, [form.amount_received, selectedInvoiceIds, invoices]);

  const columns = [
    {
      key: "payment_number",
      label: "Payment No.",
    },
    {
      key: "payment_date",
      label: "Date",
      render: (row: any) =>
        row.payment_date ? formatDateOnly(row.payment_date) : "-",
    },
    {
      key: "company_name",
      label: "Customer",
    },
    {
      key: "receive_in",
      label: "Receive In",
    },
    {
      key: "amount_received",
      label: "Amount Received",
      render: (row: any) => formatCurrency(Number(row.amount_received || 0)),
    },
    {
      key: "allocated_amount",
      label: "Applied Amount",
      render: (row: any) => formatCurrency(Number(row.allocated_amount || 0)),
    },
    {
      key: "unallocated_amount",
      label: "Unapplied Amount",
      render: (row: any) => formatCurrency(Number(row.unallocated_amount || 0)),
    },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {String(row.status || "").replace(/\b\w/g, (character) =>
            character.toUpperCase(),
          )}
        </span>
      ),
    },
  ];

  const openAdd = () => {
    setForm(createInitialForm());
    setInvoices([]);
    setSelectedInvoiceIds([]);
    setAdding(true);
  };

  const cancelAdd = () => {
    if (saving) {
      return;
    }

    setAdding(false);
    setInvoices([]);
    setSelectedInvoiceIds([]);
    setForm(createInitialForm());
  };

  const chooseCustomer = async (customerId: string): Promise<void> => {
    setForm((previous) => ({
      ...previous,
      customer_id: customerId,
    }));

    setInvoices([]);
    setSelectedInvoiceIds([]);

    if (!customerId) {
      return;
    }

    try {
      const openInvoices = await runBusy(() =>
        gtApi.openInvoices(Number(customerId)),
      );

      setInvoices(Array.isArray(openInvoices) ? openInvoices : []);
    } catch (error: any) {
      console.error("Failed to load customer invoices:", error);

      setInvoices([]);

      showModal({
        type: "error",
        title: "Unable to Load Invoices",
        message:
          error?.response?.data?.message ||
          "Unable to load the customer's outstanding Sales Invoices.",
        confirmText: "OK",
      });
    }
  };

  const toggleInvoice = (invoiceId: number) => {
    setSelectedInvoiceIds((previous) => {
      if (previous.includes(invoiceId)) {
        return previous.filter((id) => id !== invoiceId);
      }

      /*
       * Add to the end so the original
       * click order is retained.
       */
      return [...previous, invoiceId];
    });
  };

  const selectAllInvoices = () => {
    setSelectedInvoiceIds(invoices.map((invoice) => Number(invoice.id)));
  };

  const clearKnockOff = () => {
    setSelectedInvoiceIds([]);
  };

  const submit = async (): Promise<void> => {
    if (!form.customer_id) {
      showModal({
        type: "error",
        title: "Customer Required",
        message: "Please select a Customer.",
        confirmText: "OK",
      });

      return;
    }

    const amountReceived = Number(form.amount_received || 0);

    if (!Number.isFinite(amountReceived) || amountReceived <= 0) {
      showModal({
        type: "error",
        title: "Payment Amount Required",
        message: "Amount Received must be greater than zero.",
        confirmText: "OK",
      });

      return;
    }

    if (!form.payment_date) {
      showModal({
        type: "error",
        title: "Payment Date Required",
        message: "Please select the Payment Date.",
        confirmText: "OK",
      });

      return;
    }

    if (form.receive_in === "CASH" && !form.receiver_name.trim()) {
      showModal({
        type: "error",
        title: "Receiver Required",
        message: "Please enter the Receiver Name.",
        confirmText: "OK",
      });

      return;
    }

    if (form.receive_in === "BANK" && !form.bank_name.trim()) {
      showModal({
        type: "error",
        title: "Bank Required",
        message: "Please enter the Bank Name.",
        confirmText: "OK",
      });

      return;
    }

    const allocations: KnockOffAllocation[] = selectedInvoiceIds
      .map((invoiceId) => ({
        sales_invoice_id: invoiceId,
        allocated_amount: knockOff.allocationMap.get(invoiceId) || 0,
      }))
      .filter((allocation) => allocation.allocated_amount > 0);

    try {
      setSaving(true);

      const response = await runBusy(() =>
        gtApi.createPayment({
          customer_id: Number(form.customer_id),

          description: form.description.trim() || "Payment for A/c",

          receive_in: form.receive_in,

          payment_date: form.payment_date,

          amount_received: amountReceived,

          receiver_name: form.receiver_name.trim(),

          bank_name: form.bank_name.trim(),

          reference_number: form.reference_number.trim(),

          allocations,
        }),
      );

      showModal({
        type: "success",
        title: "Customer Payment Saved",
        message:
          response?.message ||
          `Customer Payment saved successfully. Unapplied Amount: ${formatCurrency(
            knockOff.unappliedAmount,
          )}`,
        confirmText: "OK",
      });

      setAdding(false);
      setInvoices([]);
      setSelectedInvoiceIds([]);
      setForm(createInitialForm());

      await load();
    } catch (error: any) {
      console.error("Create Customer Payment Error:", error);

      showModal({
        type: "error",
        title: "Unable to Save Payment",
        message:
          error?.response?.data?.message ||
          "Unable to save the Customer Payment.",
        confirmText: "OK",
      });
    } finally {
      setSaving(false);
    }
  };

  if (adding) {
    const customer = customers.find(
      (row) => Number(row.id) === Number(form.customer_id),
    );

    return (
      <div className="gt-card">
        <div className="gt-document-title-row">
          <div>
            <h2>NEW CUSTOMER PAYMENT</h2>

            {customer && (
              <span className="gt-document-number">
                {customer.company_name}
              </span>
            )}
          </div>

          <div className="gt-payment-header-summary">
            <div>
              <span>Amount Received</span>

              <strong>
                {formatCurrency(Number(form.amount_received || 0))}
              </strong>
            </div>

            <div>
              <span>Applied Amount</span>

              <strong>{formatCurrency(knockOff.appliedAmount)}</strong>
            </div>

            <div className={knockOff.unappliedAmount > 0 ? "unapplied" : ""}>
              <span>Unapplied Amount</span>

              <strong>{formatCurrency(knockOff.unappliedAmount)}</strong>
            </div>
          </div>
        </div>

        <div className="gt-form-grid">
          <label>
            Customer
            <select
              value={form.customer_id}
              disabled={saving}
              onChange={(event) => void chooseCustomer(event.target.value)}
            >
              <option value="">Select Customer</option>

              {customers
                .filter((customer) => customer.status === "active")
                .map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.company_name}
                  </option>
                ))}
            </select>
          </label>

          <label>
            Description
            <input
              value={form.description}
              disabled={saving}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  description: event.target.value,
                }))
              }
            />
          </label>

          <label>
            Receive In
            <select
              value={form.receive_in}
              disabled={saving}
              onChange={(event) => {
                const receiveIn = event.target.value as ReceiveInType;

                setForm((previous) => ({
                  ...previous,
                  receive_in: receiveIn,

                  receiver_name:
                    receiveIn === "CASH" ? previous.receiver_name : "",

                  bank_name: receiveIn === "BANK" ? previous.bank_name : "",

                  reference_number:
                    receiveIn === "BANK" ? previous.reference_number : "",
                }));
              }}
            >
              <option value="CASH">CASH</option>

              <option value="BANK">BANK</option>
            </select>
          </label>

          {form.receive_in === "CASH" ? (
            <label>
              Receiver Name
              <input
                value={form.receiver_name}
                disabled={saving}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    receiver_name: event.target.value,
                  }))
                }
              />
            </label>
          ) : (
            <>
              <label>
                Bank Name
                <input
                  value={form.bank_name}
                  disabled={saving}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      bank_name: event.target.value,
                    }))
                  }
                />
              </label>

              <label>
                Reference No.
                <input
                  value={form.reference_number}
                  disabled={saving}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      reference_number: event.target.value,
                    }))
                  }
                />
              </label>
            </>
          )}

          <label>
            Payment Date
            <input
              type="date"
              value={form.payment_date}
              disabled={saving}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  payment_date: event.target.value,
                }))
              }
            />
          </label>

          <label>
            Amount Received
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.amount_received}
              disabled={saving}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  amount_received: event.target.value,
                }))
              }
            />
          </label>
        </div>

        <div className="gt-knockoff-section">
          <div className="gt-knockoff-header">
            <div>
              <h3>KNOCK-OFF SALES INVOICES</h3>

              <p>
                Tick the Sales Invoices you want to settle. The system will
                automatically apply the available payment in the order selected.
              </p>
            </div>

            <div className="gt-knockoff-actions">
              <button
                type="button"
                className="btn-secondary"
                disabled={saving || !invoices.length}
                onClick={selectAllInvoices}
              >
                Select All
              </button>

              <button
                type="button"
                className="btn-secondary"
                disabled={saving || !selectedInvoiceIds.length}
                onClick={clearKnockOff}
              >
                Clear
              </button>
            </div>
          </div>

          <div className="gt-knockoff-table-wrap">
            <table className="gt-knockoff-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Invoice Date</th>
                  <th>Due Date</th>
                  <th>Invoice Amount</th>
                  <th>Already Paid</th>
                  <th>Outstanding</th>
                  <th>Knocked-Off</th>
                  <th className="gt-knockoff-check-column">Knock-Off</th>
                </tr>
              </thead>

              <tbody>
                {!form.customer_id ? (
                  <tr>
                    <td colSpan={8} className="gt-knockoff-empty">
                      Select a Customer to load outstanding Sales Invoices.
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="gt-knockoff-empty">
                      No outstanding submitted Sales Invoices found for this
                      Customer.
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => {
                    const checked = selectedInvoiceIds.includes(
                      Number(invoice.id),
                    );

                    const knockedAmount =
                      knockOff.allocationMap.get(Number(invoice.id)) || 0;

                    const balanceAfter = round2(
                      Math.max(
                        Number(invoice.outstanding_amount || 0) - knockedAmount,
                        0,
                      ),
                    );

                    return (
                      <tr
                        key={invoice.id}
                        className={checked ? "selected" : ""}
                      >
                        <td>
                          <strong>{invoice.sales_invoice_number}</strong>
                        </td>

                        <td>
                          {invoice.invoice_date
                            ? formatDateOnly(invoice.invoice_date)
                            : "-"}
                        </td>

                        <td>
                          {invoice.due_date
                            ? formatDateOnly(invoice.due_date)
                            : "-"}
                        </td>

                        <td>
                          {formatCurrency(Number(invoice.grand_total || 0))}
                        </td>

                        <td>
                          {formatCurrency(Number(invoice.paid_amount || 0))}
                        </td>

                        <td>
                          <div className="gt-knockoff-balance-cell">
                            <strong>
                              {formatCurrency(
                                Number(invoice.outstanding_amount || 0),
                              )}
                            </strong>

                            {checked && knockedAmount > 0 && (
                              <small>
                                Balance: {formatCurrency(balanceAfter)}
                              </small>
                            )}
                          </div>
                        </td>

                        <td>
                          <strong
                            className={
                              knockedAmount > 0 ? "gt-knocked-value" : ""
                            }
                          >
                            {formatCurrency(knockedAmount)}
                          </strong>
                        </td>

                        <td className="gt-knockoff-check-column">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={saving}
                            onChange={() => toggleInvoice(Number(invoice.id))}
                            aria-label={`Knock off ${invoice.sales_invoice_number}`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="gt-payment-summary">
          <div>
            <span>Amount Received</span>

            <strong>{formatCurrency(Number(form.amount_received || 0))}</strong>
          </div>

          <div>
            <span>Applied Amount</span>

            <strong>{formatCurrency(knockOff.appliedAmount)}</strong>
          </div>

          <div className="gt-payment-unapplied">
            <span>Unapplied Amount</span>

            <strong>{formatCurrency(knockOff.unappliedAmount)}</strong>
          </div>
        </div>

        <div className="gt-footer">
          <div>
            <button
              type="button"
              className="btn-secondary"
              disabled={saving}
              onClick={cancelAdd}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn-primary"
              disabled={saving}
              onClick={() => void submit()}
            >
              {saving ? "Saving..." : "Save Payment"}
            </button>
          </div>

          <div className="gt-payment-footer-totals">
            <span>
              Applied: <strong>{formatCurrency(knockOff.appliedAmount)}</strong>
            </span>

            <span>
              Unapplied:{" "}
              <strong>{formatCurrency(knockOff.unappliedAmount)}</strong>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminListToolkit
      title="CUSTOMER PAYMENTS"
      columns={columns}
      data={rows}
      selectedRow={selected}
      onAdd={openAdd}
      enableExport
      exportFileName="customer-payments"
      extraButtons={[
        {
          label: "PDF",
          onClick: () => {
            if (!selected) {
              showModal({
                type: "error",
                title: "Customer Payment Required",
                message: "Please select a Customer Payment first.",
                confirmText: "OK",
              });

              return;
            }

            window.open(
              gtApi.pdfUrl("customer-payments", Number(selected.id)),
              "_blank",
            );
          },
        },
      ]}
    >
      {(filtered) => (
        <DataTable
          columns={columns}
          data={filtered}
          selectedRowId={selected?.id}
          onRowSelect={setSelected}
        />
      )}
    </AdminListToolkit>
  );
}
